import type { ReactNode } from "react"
import {
	IconCodeBranchOutline18 as Branch,
	IconCheckOutline18 as Check,
	IconEnvelopeOutline18 as Envelope,
	IconEnvelopeArrowRightOutline18 as EnvelopeSend,
} from "nucleo-ui-outline-18"
import Balancer from "react-wrap-balancer"

import { Badge, Button, Input, Textarea } from "@productlane/ui"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

const HEADLINE = "Connect your codebase"

const DEV_EMAIL = `Hi,

we use Productlane for customer support. To let our AI agent answer questions straight from our code, it needs read access to our GitHub repo once.

It takes two minutes: [Connect GitHub]

Thanks!
Raphael`

function Slide({ children }: { children: ReactNode }) {
	return (
		<div className="mx-auto w-full max-w-[34rem] rounded-xl bg-white px-8 py-10 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="flex flex-col items-center text-center">{children}</div>
		</div>
	)
}

function Title({ subtitle }: { subtitle: string }) {
	return (
		<>
			<div className="mb-1 text-3xl font-bold tracking-tight">{HEADLINE}</div>
			<Balancer className="tracking-tight text-secondary">{subtitle}</Balancer>
		</>
	)
}

function SkipRow() {
	return (
		<Button
			variant="link"
			className="mt-1 w-full max-w-xxs opacity-70 hover:opacity-80"
		>
			{"I'll do this later"}
		</Button>
	)
}

function EmailPreview({ to }: { to?: string }) {
	return (
		<div className="w-full rounded-lg text-left ring-1 ring-black/5 dark:ring-white/10">
			<div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 text-[13px] text-tertiary dark:border-gray-700/80">
				<Envelope className="size-4 flex-none" />
				<span className="truncate">
					{to ?? "dev@acme.com"} · Read access to GitHub for our support agent
				</span>
			</div>
			<Textarea
				name="dev-email"
				variant="borderless"
				noResize
				inputClassName="min-h-[11rem] px-3 py-2.5 text-[13px] leading-relaxed"
				defaultValue={DEV_EMAIL}
			/>
		</div>
	)
}

// 1. Two equal paths: connect it yourself, or hand it to whoever owns the repo.
export function TwoPaths() {
	return (
		<Slide>
			<Title subtitle="Your agent answers from the code, not only from docs. Connect GitHub yourself, or send it to the developer who can." />

			<div className="mt-8 grid w-full grid-cols-1 gap-3 text-left sm:grid-cols-2">
				<div className="flex flex-col rounded-lg bg-black/[0.02] p-4 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<GitHubIcon className="size-[18px] flex-none" />
					<div className="mt-3 text-[0.9rem] font-medium">I have access</div>
					<p className="mt-1 text-[13px] text-tertiary">
						Read-only, takes about a minute.
					</p>
					<Button className="mt-auto h-9 w-full" Icon={GitHubIcon}>
						Connect GitHub
					</Button>
				</div>

				<div className="flex flex-col rounded-lg bg-black/[0.02] p-4 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<EnvelopeSend className="size-[18px] flex-none text-tertiary" />
					<div className="mt-3 text-[0.9rem] font-medium">
						A developer has access
					</div>
					<p className="mt-1 text-[13px] text-tertiary">
						We email them a link that only connects GitHub.
					</p>
					<Input
						name="v1-dev"
						className="mt-3"
						inputClassName="h-9 rounded-lg"
						placeholder="dev@acme.com"
					/>
					<Button variant="outline" className="mt-2 h-9 w-full">
						Send invite
					</Button>
				</div>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

// 2. The email is the hero: most people setting up support do not own the repo.
export function EmailFirst() {
	return (
		<Slide>
			<Title subtitle="The best answers come from your code. Send this to the developer who owns the repo, it takes them two minutes." />

			<div className="mt-7 w-full space-y-2.5">
				<Input
					name="v2-dev"
					icon={Envelope}
					inputClassName="h-10 rounded-lg"
					placeholder="dev@acme.com"
				/>
				<EmailPreview />
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs" Icon={EnvelopeSend}>
				Send to developer
			</Button>
			<Button
				variant="link"
				className="mt-1 w-full max-w-xxs"
				Icon={GitHubIcon}
			>
				I have access, connect myself
			</Button>
		</Slide>
	)
}

// 3. Proof up front: the same question answered with and without the repo.
export function BeforeAfter() {
	return (
		<Slide>
			<Title subtitle="Here is the same customer question, answered with docs only and with your code." />

			<div className="mt-7 w-full space-y-2 text-left">
				<div className="rounded-lg bg-black/[0.02] p-3 text-[13px] text-tertiary ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<div className="mb-1 font-medium text-secondary">
						&ldquo;Why does my export stop at 1,000 rows?&rdquo;
					</div>
					Docs only: &ldquo;Exports may be limited depending on your plan.
					Contact support for details.&rdquo;
				</div>
				<div className="rounded-lg p-3 text-[13px] ring-1 ring-black/5 dark:ring-white/10">
					<div className="mb-1 flex items-center gap-2">
						<span className="font-medium">With your code</span>
						<Badge variant="green">Resolved</Badge>
					</div>
					<span className="text-secondary">
						&ldquo;Exports on the Starter plan stop at 1,000 rows. Switch to Pro
						under Settings → Billing, or filter by date and export in
						batches.&rdquo;
					</span>
				</div>
			</div>

			<div className="mt-5 flex w-full gap-2">
				<Button className="h-10 flex-1" Icon={GitHubIcon}>
					Connect GitHub
				</Button>
				<Button variant="outline" className="h-10 flex-1" Icon={EnvelopeSend}>
					Ask a developer
				</Button>
			</div>
			<SkipRow />
		</Slide>
	)
}

// 4. Waiting state: the invite is out, the slide says so and lets you move on.
export function InviteSent() {
	return (
		<Slide>
			<Title subtitle="We sent the invite. You can keep going, and we will tell you as soon as GitHub is connected." />

			<div className="mt-8 w-full space-y-2.5 text-left">
				<div className="flex items-center gap-3 rounded-lg bg-black/[0.02] p-3.5 ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<span className="flex size-5 flex-none items-center justify-center rounded-full bg-green-500 text-white">
						<Check className="size-3 flex-none" />
					</span>
					<div className="min-w-0 flex-1">
						<div className="text-[0.9rem] font-medium">
							Invite sent to jonas@acme.com
						</div>
						<p className="text-[13px] text-tertiary">Sent 2 minutes ago</p>
					</div>
					<Button variant="ghost" size="sm" className="flex-none">
						Resend
					</Button>
				</div>

				<div className="flex items-center gap-3 rounded-lg p-3.5 ring-1 ring-black/5 dark:ring-white/10">
					<span className="size-5 flex-none rounded-full ring-1 ring-gray-300 dark:ring-gray-700" />
					<div className="min-w-0 flex-1">
						<div className="text-[0.9rem] font-medium">GitHub connected</div>
						<p className="text-[13px] text-tertiary">
							Then we write an article for every screen in your app.
						</p>
					</div>
				</div>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<Button variant="link" className="mt-1 w-full max-w-xxs opacity-70">
				Connect it myself instead
			</Button>
		</Slide>
	)
}

// 5. One row in the setup checklist, expanded inline when picked.
export function ChecklistRow() {
	return (
		<Slide>
			<Title subtitle="Teams that connect GitHub resolve far more tickets with the agent. It is the one step worth doing today." />

			<div className="mt-8 w-full divide-y divide-gray-200 rounded-lg text-left ring-1 ring-black/5 dark:divide-gray-700/80 dark:ring-white/10">
				<div className="flex items-center gap-3 p-4">
					<span className="flex size-5 flex-none items-center justify-center rounded-full bg-green-500 text-white">
						<Check className="size-3 flex-none" />
					</span>
					<div className="flex-1 text-[0.9rem] font-medium">
						Help center imported
					</div>
				</div>

				<div className="p-4">
					<div className="flex items-center gap-3">
						<Branch className="size-5 flex-none text-tertiary" />
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2">
								<span className="text-[0.9rem] font-medium">
									Connect your codebase
								</span>
								<Badge variant="green">Best answers</Badge>
							</div>
						</div>
						<Button size="sm" className="flex-none" Icon={GitHubIcon}>
							Connect
						</Button>
					</div>
					<div className="mt-3 flex items-center gap-2 pl-8">
						<span className="flex-none text-[13px] text-tertiary">
							No access? Invite a developer
						</span>
						<Input
							name="v5-dev"
							className="flex-1"
							inputClassName="h-8 rounded-lg"
							placeholder="dev@acme.com"
						/>
						<Button variant="outline" size="sm" className="flex-none">
							Send
						</Button>
					</div>
				</div>
			</div>

			<Button size="xl" className="mt-6 w-full max-w-xxs">
				Continue
			</Button>
			<SkipRow />
		</Slide>
	)
}

const VARIANTS: { title: string; note: string; render: () => ReactNode }[] = [
	{
		title: "1. Two paths",
		note: "Self-connect and invite carry equal weight. Clear when we do not know who is setting up.",
		render: TwoPaths,
	},
	{
		title: "2. Email first",
		note: "Assumes the founder or support lead does not own the repo. The editable email is the main action.",
		render: EmailFirst,
	},
	{
		title: "3. Before and after",
		note: "Sells the step with one real answer. Strongest argument, but needs the example to feel like their product.",
		render: BeforeAfter,
	},
	{
		title: "4. Invite sent",
		note: "The state after variant 1 or 2. Lets them continue and promises a heads up once it is connected.",
		render: InviteSent,
	},
	{
		title: "5. Checklist row",
		note: "Lives in the existing setup checklist. The invite field sits under the connect button for whoever lacks access.",
		render: ChecklistRow,
	},
]

export function OnboardingGithubPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-dvh overflow-y-auto bg-gray-100 pb-24 dark:bg-gray-950">
			<div className="mx-auto max-w-4xl px-6 py-12">
				<h1 className="text-2xl font-semibold tracking-tight">
					Onboarding: connect GitHub or invite a developer
				</h1>
				<p className="mt-2 max-w-2xl text-[15px] text-secondary">
					Five ways to make GitHub part of the first onboarding, with a
					one-click email for the developer who owns the repo.
				</p>

				<div className="mt-10 space-y-16">
					{VARIANTS.map((variant) => (
						<section key={variant.title}>
							<div className="mb-4">
								<div className="text-[0.95rem] font-medium">
									{variant.title}
								</div>
								<p className="text-[13px] text-tertiary">{variant.note}</p>
							</div>
							{variant.render()}
						</section>
					))}
				</div>
			</div>
		</div>
	)
}
